import React, { useState } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const EmotionCharadesGame = ({ gameData, onComplete, onRegenerate }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [feedback, setFeedback] = useState(null); 

    const totalSequences = gameData?.sequences?.length || 0;
    const currentSequence = gameData?.sequences?.[currentIndex];
    const pointsPerSequence = gameData?.points_per_sequence || 25;

    const handleOptionSelect = (index) => {
        if (selectedOption !== null || feedback) return;

        triggerHaptic(30);
        setSelectedOption(index);
        const isCorrect = index === currentSequence.correct_option_index;

        if (isCorrect) {
            const newScore = score + pointsPerSequence;
            setScore(newScore);
            setFeedback({ status: 'correct', sequence: currentSequence });
            triggerHaptic([100, 50, 100]);
        } else {
            setFeedback({ status: 'wrong', sequence: currentSequence });
            triggerHaptic([50, 50, 50]);
        }

        setTimeout(() => {
            setFeedback(null);
            setSelectedOption(null);

            if (currentIndex < totalSequences - 1) {
                setCurrentIndex(prev => prev + 1);
            } else {
                triggerHaptic([200, 100, 200, 100, 300]);
                onComplete?.(score + (isCorrect ? pointsPerSequence : 0));
            }
        }, 5000); 
    };

    if (!gameData || !gameData.sequences || !currentSequence) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-purple-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">🎭</div>
                <h3 className="text-base md:text-3xl font-black text-purple-900 tracking-tighter uppercase">Scena ruošiama...</h3>
            </div>
        );
    }

    if (!currentSequence.options || !Array.isArray(currentSequence.options)) {
        return (
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-16 text-center shadow-2xl border-purple-100 animate-pop-in max-w-2xl mx-auto mt-10 bg-white">
                <div className="text-5xl md:text-[120px] mb-8 animate-float">🎭</div>
                <h3 className="text-lg md:text-4xl font-black text-purple-600 mb-6 uppercase tracking-tighter">Spektaklis Atsinaujino!</h3>
                <p className="text-slate-500 text-sm md:text-xl font-bold mb-12 italic leading-relaxed font-serif">
                    Atnaujinome emocijų scenarijus, kad jie būtų dar gilesni. Prašome sugeneruoti sceną iš naujo.
                </p>
                <button 
                    onClick={() => onRegenerate ? onRegenerate() : window.location.reload()} 
                    className="premium-button-3d px-8 py-3 md:px-12 md:py-8 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl md:rounded-[3rem] font-black uppercase tracking-widest shadow-2xl scale-110 active:scale-95 transition-all"
                >
                    🚀 SUGENERUOTI SCENĄ
                </button>
            </div>
        );
    }

    const progress = ((currentIndex) / totalSequences) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-purple-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-purple-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        🎭
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Jausmų Teatras
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Empatija</p>
                        <p className="text-sm md:text-2xl font-black text-purple-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Scena</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentIndex + 1}/{totalSequences}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-purple-400 via-pink-500 to-rose-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(168,85,247,0.3)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="flex flex-col items-center justify-center py-4 md:py-12">
            <div className="premium-glass rounded-[2rem] md:rounded-[4rem] p-4 md:p-14 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center w-full max-w-2xl text-center">
                <div className="absolute top-0 right-0 p-4 md:p-8 text-6xl md:text-8xl opacity-[0.03] select-none pointer-events-none">📜</div>
                <h4 className="text-[10px] font-black text-purple-400 uppercase tracking-[0.5em] mb-4 md:mb-10 text-center">Herojaus Istorija</h4>
                
                <div className="relative group">
                    <div className="absolute -inset-10 bg-purple-200 rounded-full blur-3xl opacity-20 animate-pulse"></div>
                    <div className="bg-white px-6 py-8 md:px-10 md:py-16 rounded-2xl md:rounded-[4rem] shadow-2xl border-4 border-purple-50 relative z-10 scale-105 group-hover:scale-110 transition-transform duration-500">
                        <div className="absolute top-2 left-4 md:top-8 md:left-8 text-2xl md:text-6xl text-purple-100 opacity-30 select-none font-serif leading-none">“</div>
                        <p className="text-base md:text-3xl font-black text-slate-800 leading-snug md:leading-[1.4] tracking-tight italic font-serif text-center break-words px-2 md:px-4">
                            {currentSequence.story}
                        </p>
                        <div className="absolute bottom-4 right-4 md:bottom-8 md:right-8 text-2xl md:text-6xl text-purple-100 opacity-30 select-none font-serif leading-none rotate-180">“</div>
                    </div>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="flex flex-col gap-3 md:gap-4 w-full">
            <div className="flex items-center gap-2 px-2">
                <div className="w-2 h-2 md:w-3 md:h-3 bg-purple-500 rounded-full animate-pulse"></div>
                <h4 className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">Pasirink teisingą jausmų seką</h4>
            </div>
            {currentSequence.options.map((option, index) => {
                const isSelected = selectedOption === index;
                const isCorrectInFeed = feedback?.status === 'correct' && isSelected;
                const isWrongInFeed = feedback?.status === 'wrong' && isSelected;
                const isCorrectOther = feedback && index === currentSequence.correct_option_index && !isSelected;

                return (
                    <button
                        key={index}
                        onClick={() => handleOptionSelect(index)}
                        disabled={selectedOption !== null}
                        className={`
                            premium-button-3d relative rounded-xl md:rounded-[3rem] p-4 md:p-8 flex items-center justify-center text-center transition-all duration-500 border-b-8 md:border-b-[12px]
                            ${isCorrectInFeed
                                ? 'bg-emerald-500 text-white border-emerald-700 scale-105 shadow-[0_0_50px_rgba(16,185,129,0.5)]'
                                : isWrongInFeed
                                    ? 'bg-rose-500 text-white border-rose-700 animate-shake shadow-[0_0_50px_rgba(244,63,94,0.5)]'
                                    : isCorrectOther
                                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                                        : isSelected
                                            ? 'bg-purple-600 text-white shadow-2xl border-purple-800'
                                            : 'bg-slate-800/80 border-slate-700 text-white hover:bg-slate-800 hover:border-purple-500/50 active:translate-y-1 active:border-b-0'}
                            disabled:cursor-default
                        `}
                    >
                        <div className="text-sm md:text-3xl font-black tracking-[0.2em] md:tracking-[0.4em] leading-none text-center">
                            {option}
                        </div>
                        {isCorrectInFeed && (
                            <div className="absolute top-2 right-2 md:top-4 md:right-4 w-8 h-8 md:w-12 md:h-12 bg-white text-emerald-600 rounded-full flex items-center justify-center text-lg md:text-2xl shadow-xl border-2 md:border-4 border-emerald-100 animate-pop-in">✓</div>
                        )}
                    </button>
                );
            })}
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-6 md:p-12 rounded-[2.5rem] md:rounded-[4rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
                        ${feedback.status === 'correct' ? 'border-emerald-500' : 'border-purple-500'}
                    `}>
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback.status === 'correct' ? '🎭' : '🧐'}
                        </div>
                        
                        <div className="text-center space-y-4 pt-4">
                            <h2 className={`text-xl md:text-5xl font-black uppercase tracking-tighter ${feedback.status === 'correct' ? 'text-emerald-600' : 'text-purple-600'}`}>
                                {feedback.status === 'correct' ? 'TIKSLUS JAUTRUMAS!' : 'EMOCINĖ ĮŽVALGA'}
                            </h2>

                            <div className={`p-4 md:p-10 rounded-xl md:rounded-[3rem] border-2 text-left italic font-serif leading-relaxed ${feedback.status === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-purple-50 border-purple-100'}`}>
                                <p className="text-sm md:text-2xl font-bold text-slate-700 text-center leading-relaxed italic">
                                    "{feedback.sequence.explanation}"
                                </p>
                            </div>

                            <div className="flex items-center justify-center gap-4">
                                <div className="flex gap-2">
                                    <div className="w-2 h-2 md:w-3 md:h-3 bg-slate-200 rounded-full animate-bounce" />
                                    <div className="w-2 h-2 md:w-3 md:h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]" />
                                    <div className="w-2 h-2 md:w-3 md:h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]" />
                                </div>
                                <span className="text-[10px] md:text-sm font-black text-slate-300 uppercase tracking-[0.3em]">
                                    {feedback.status === 'correct' ? 'Kita scena...' : 'Tęsiame tyrimą...'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default EmotionCharadesGame;
